import { FC, useRef } from 'react';
import type { LottieOptions, LottieRef } from 'lottie-react';
import styled from 'styled-components';
import LottieIcon from '../LottieIcon';
import { useLottieOffscreenPause } from '../../hooks/useLottieOffscreenPause';
import { useColorTheme, useFixedSurface } from '../../providers/Theme';
import spinnerDotsWhite from './assets/spinnerDotsWhite.json';
import spinnerDotsDark from './assets/spinnerDotsDark.json';
import spinnerDotsDarkBlue from './assets/spinnerDotsDarkBlue.json';

/** Names the dots' colour: `light` is white dots, for dark surfaces. */
export type tSpinnerVariant = 'dark' | 'darkBlue' | 'light';

export interface ISpinnerProps {
  className?: string;
  variant?: tSpinnerVariant;
}

const SPINNER_ANIMATIONS: Record<tSpinnerVariant, LottieOptions['animationData']> = {
  light: spinnerDotsWhite,
  dark: spinnerDotsDark,
  darkBlue: spinnerDotsDarkBlue
};

const Spinner: FC<ISpinnerProps> = ({ className, variant }) => {
  const { theme } = useColorTheme();
  const isFixedSurface = useFixedSurface();
  const resolved = variant ?? (theme === 'light' && !isFixedSurface ? 'dark' : 'light');

  const lottieRef: LottieRef = useRef(null);
  // A variant change swaps the (module-constant) animation data, which rebuilds the
  // observer; its initial notification re-pauses the autoplaying reload off-screen.
  const animationData = SPINNER_ANIMATIONS[resolved];
  useLottieOffscreenPause(lottieRef, animationData);

  return (
    <Wrapper className={className}>
      <LottieIcon
        lottieRef={lottieRef}
        animationJSONData={animationData}
        loop
        autoPlay
        speed={2}
        height="36px"
        width="72px"
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default Spinner;
